import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { FileCheck2, Upload, Trash2, ShieldAlert, CheckCircle2, Clock, XCircle } from 'lucide-react';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import { api, formatError } from '../lib/api';
import { toast } from 'sonner';

const STATUS_UI = {
  current: { icon: <CheckCircle2 size={16} className="text-[#2A9D8F]" />, label: 'Current', color: 'text-[#2A9D8F] bg-[#2A9D8F]/10' },
  expiring: { icon: <Clock size={16} className="text-[#E9C46A]" />, label: 'Expiring soon', color: 'text-[#B08911] bg-[#E9C46A]/20' },
  expired: { icon: <XCircle size={16} className="text-[#E76F51]" />, label: 'Expired', color: 'text-[#E76F51] bg-[#E76F51]/10' },
  'no-expiry': { icon: <FileCheck2 size={16} className="text-[#5C5B5A]" />, label: 'No expiry', color: 'text-[#5C5B5A] bg-[#F3F2EE]' },
};

export default function Compliance() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [docTypes, setDocTypes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ doc_type: 'license', file: null, expiry_date: '', notes: '' });

  const load = async () => {
    try {
      const [s, t] = await Promise.all([api.myCompliance(), api.docTypes()]);
      setStatus(s.data);
      setDocTypes(t.data);
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); }, []);

  if (user && user.role !== 'tradie') return <Navigate to="/" replace />;

  const upload = async (e) => {
    e.preventDefault();
    if (!form.file) return toast.error('Choose a file first');
    setBusy(true);
    try {
      const reader = new FileReader();
      const b64 = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(String(reader.result).split(',')[1]);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(form.file);
      });
      await api.uploadCompliance({
        doc_type: form.doc_type,
        filename: form.file.name,
        mime_type: form.file.type || 'application/octet-stream',
        data_b64: b64,
        expiry_date: form.expiry_date || null,
        notes: form.notes || null,
      });
      toast.success('Document uploaded');
      setForm({ doc_type: 'license', file: null, expiry_date: '', notes: '' });
      document.getElementById('compliance-file').value = '';
      load();
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.deleteCompliance(id); toast.success('Removed'); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const overallColor = {
    current: 'text-[#2A9D8F] bg-[#2A9D8F]/10 border-[#2A9D8F]/30',
    expiring: 'text-[#B08911] bg-[#E9C46A]/20 border-[#E9C46A]',
    expired: 'text-[#E76F51] bg-[#E76F51]/10 border-[#E76F51]/40',
    missing: 'text-[#8A8988] bg-[#F3F2EE] border-[#E5E4E0]',
    unknown: 'text-[#8A8988] bg-[#F3F2EE] border-[#E5E4E0]',
  }[status?.state || 'unknown'];

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="compliance-page">
      <Header />
      <div className="mx-auto max-w-5xl px-6 py-10 md:px-10">
        <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#D96C4E]">Compliance Vault</div>
        <h1 className="mt-2 font-heading text-4xl font-bold tracking-tight text-[#1C1B1A]">Your paperwork, always current</h1>
        <p className="mt-2 max-w-2xl text-[#5C5B5A]">
          Upload your license, insurance, workers comp and any other tickets. Real estate agencies see a live status — you win jobs while your competitors chase paperwork.
        </p>

        {status && (
          <div className={`mt-8 flex items-center gap-4 border p-5 ${overallColor}`} data-testid="compliance-overall">
            <ShieldAlert size={28} />
            <div className="flex-1">
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em]">Overall status</div>
              <div className="font-heading text-2xl font-bold uppercase tracking-wide">{status.state}</div>
            </div>
            <div className="text-right text-xs">
              <div><b>{status.valid_count}</b> current</div>
              <div><b>{status.expiring_count}</b> expiring</div>
              <div><b>{status.expired_count}</b> expired</div>
              {status.missing_types?.length > 0 && <div className="mt-1 opacity-70">Missing: {status.missing_types.join(', ')}</div>}
            </div>
          </div>
        )}

        <form onSubmit={upload} className="mt-8 grid grid-cols-1 gap-3 md:grid-cols-4" data-testid="upload-form">
          <select value={form.doc_type} onChange={(e) => setForm({ ...form, doc_type: e.target.value })}
            data-testid="doc-type-select" className="input-editorial">
            {docTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <input id="compliance-file" type="file" accept="image/*,application/pdf"
            onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })}
            data-testid="doc-file" className="input-editorial" />
          <input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })}
            data-testid="doc-expiry" className="input-editorial" placeholder="Expiry date" />
          <button type="submit" disabled={busy || !form.file} data-testid="upload-btn" className="btn-primary justify-center disabled:opacity-50">
            <Upload size={16} /> {busy ? 'Uploading...' : 'Upload'}
          </button>
        </form>

        <div className="mt-8 space-y-3" data-testid="docs-list">
          {(!status?.docs || status.docs.length === 0) && (
            <div className="border border-[#E5E4E0] bg-white p-8 text-center text-[#8A8988]">No documents yet. Upload your license first.</div>
          )}
          {status?.docs?.map((d) => {
            const s = STATUS_UI[d.status] || STATUS_UI['no-expiry'];
            return (
              <div key={d.id} className="flex items-center justify-between border border-[#E5E4E0] bg-white p-5" data-testid={`doc-${d.id}`}>
                <div className="flex items-center gap-4">
                  <FileCheck2 size={24} className="text-[#1A362D]" />
                  <div>
                    <div className="font-heading text-lg font-bold text-[#1C1B1A]">{d.doc_type_name}</div>
                    <div className="text-xs text-[#8A8988]">{d.filename}{d.expiry_date && ` · expires ${d.expiry_date}`}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.15em] ${s.color}`}>
                    {s.icon} {s.label}
                  </span>
                  <button onClick={() => remove(d.id)} data-testid={`del-${d.id}`} className="p-2 text-[#8A8988] hover:text-[#E76F51]">
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
